// ── Browser → relay HTTP helper for Polymarket orders ───────────
//
// Thin wrapper around `/api/polymarket/orders` (the trader-facing relay
// route that proxies to the org's stored Polymarket credential). Use
// this for org-key mode trading. The browser-side `placePolyOrder` in
// ./client.ts is still the right path for the personal-key flow that
// signs orders with a localStorage-stored EVM private key.
//
// The relay returns a discriminating `code` on 404 so we can give the
// user actionable copy instead of "Polymarket trading is unavailable":
//   NOT_IN_ORG          → "You are not part of an org…"
//   PERSONAL_KEY_MODE   → "Personal-key mode — flip useOrgKeys…"
//   NO_POLY_CRED        → "Org has no Polymarket key — ask an admin…"
//
// Other failures (5xx / 502 from the underlying SDK call) propagate
// with the relay's `error` string intact.

import { apiFetch } from '@/stores/authStore';
import {
  isLimitRejectionPayload,
  type LimitRejectionBreach,
  type LimitRejectionCode,
} from '@/lib/limits';

export type PolyRelayUnavailableCode =
  | 'NOT_IN_ORG'
  | 'PERSONAL_KEY_MODE'
  | 'NO_POLY_CRED'
  | 'ONLY_LEGACY_EOA_CRED';
export type PolyRelayOrderErrorCode = PolyRelayUnavailableCode | LimitRejectionCode | 'POLYMARKET_GEOBLOCK';

export interface PlacePolyOrderRelayArgs {
  tokenId: string;
  action: 'buy' | 'sell';
  priceCents: number;
  contracts: number;
  postOnly?: boolean;
  tickSize?: string;
  negRisk?: boolean;
  /** Unix seconds. When > 0, the relay submits as GTD and the CLOB
   *  auto-cancels at this time; omit or 0 → GTC. */
  expirationTs?: number;
  /** 'FAK' (IOC): fill whatever crosses at the limit price, cancel the
   *  remainder. Used by the close path; mutually exclusive with
   *  postOnly/expirationTs. */
  timeInForce?: 'FAK';
}

export type PlacePolyOrderRelayResult =
  | { ok: true; result: unknown }
  | {
      ok: false;
      status: number;
      error: string;
      code?: PolyRelayOrderErrorCode;
      /** Present when the relay rejected the order at the limit chokepoint
       *  (status 403, code LIMIT_*). Lets UIs render a richer "would exceed
       *  your cap" message than the bare error string. */
      breach?: LimitRejectionBreach;
    };

/**
 * POST to `/api/polymarket/orders`. Returns a discriminated union so
 * callers don't have to throw/catch to distinguish "Polymarket trading
 * isn't available right now (here's why)" from "the order itself was
 * rejected."
 */
export async function placePolyOrderViaRelay(
  args: PlacePolyOrderRelayArgs,
): Promise<PlacePolyOrderRelayResult> {
  let res: Response;
  try {
    res = await apiFetch('/api/polymarket/orders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(args),
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    return { ok: false, status: 0, error: message };
  }

  let body: { result?: unknown; error?: string; code?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    // Body wasn't JSON — leave body empty and fall through to status-based error.
  }

  if (res.ok) {
    return { ok: true, result: body.result };
  }

  // Limit rejection (403 from the relay chokepoint) carries a structured
  // breach payload; surface it on the result so UIs can render richer text.
  if (res.status === 403 && isLimitRejectionPayload(body)) {
    return {
      ok: false,
      status: res.status,
      error: body.error,
      code: body.code,
      breach: body.breach,
    };
  }

  const code =
    body.code === 'NOT_IN_ORG' ||
    body.code === 'PERSONAL_KEY_MODE' ||
    body.code === 'NO_POLY_CRED' ||
    body.code === 'ONLY_LEGACY_EOA_CRED' ||
    body.code === 'POLYMARKET_GEOBLOCK'
      ? body.code
      : undefined;

  return {
    ok: false,
    status: res.status,
    error: body.error ?? `Polymarket relay failed (${res.status})`,
    code,
  };
}

/**
 * Map an unavailable-trading code into actionable user copy. Returns
 * null for unknown codes so the caller can fall back to the raw error
 * message. The strings are user-facing — keep them short and concrete.
 */
export function describePolyRelayUnavailable(code: PolyRelayUnavailableCode): string {
  switch (code) {
    case 'NOT_IN_ORG':
      return 'Polymarket org trading requires you to be in an organization. Ask an admin to add you.';
    case 'PERSONAL_KEY_MODE':
      return 'Polymarket relay trading is only available in org-key mode. Switch in Settings.';
    case 'NO_POLY_CRED':
      return 'Your org does not have a Polymarket key configured. Ask an admin or manager to add one in Manager → Org Polymarket Key.';
    case 'ONLY_LEGACY_EOA_CRED':
      return 'Your org\'s only Polymarket keys are legacy EOA (type 0), which Polymarket no longer accepts. An admin must re-register the key with its account\'s signature type + funder (see the guide in Org Settings → Add Key).';
  }
}

// ── Open orders read + cancel ──────────────────────────────────
// The org's CLOB client lives on the relay; these mirror the
// place-order helper above for the rest of the order lifecycle.

/** Shape the SDK returns on getOpenOrders — fields are loose since
 *  CLOB's response is not strictly typed in our SDK version. */
export interface PolyRelayOpenOrder {
  id?: string;
  orderID?: string;
  orderId?: string;
  market?: string;
  asset_id?: string;
  side?: string;
  price?: number | string;
  size?: number | string;
  original_size?: number | string;
  size_matched?: number | string;
  timestamp?: number | string;
  [key: string]: unknown;
}

export type ListPolyOpenOrdersResult =
  | { ok: true; orders: PolyRelayOpenOrder[] }
  | { ok: false; status: number; error: string; code?: PolyRelayUnavailableCode };

export async function listPolyOpenOrdersViaRelay(): Promise<ListPolyOpenOrdersResult> {
  let res: Response;
  try {
    res = await apiFetch('/api/polymarket/orders', { method: 'GET' });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    return { ok: false, status: 0, error: message };
  }

  let body: { orders?: unknown; error?: string; code?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* non-JSON body */
  }

  if (res.ok) {
    const orders = Array.isArray(body.orders) ? (body.orders as PolyRelayOpenOrder[]) : [];
    return { ok: true, orders };
  }

  const code =
    body.code === 'NOT_IN_ORG' ||
    body.code === 'PERSONAL_KEY_MODE' ||
    body.code === 'NO_POLY_CRED' ||
    body.code === 'ONLY_LEGACY_EOA_CRED'
      ? body.code
      : undefined;

  return {
    ok: false,
    status: res.status,
    error: body.error ?? `Polymarket relay failed (${res.status})`,
    code,
  };
}

export type CancelPolyOrderRelayResult =
  | { ok: true; result: unknown }
  | { ok: false; status: number; error: string; code?: PolyRelayUnavailableCode };

// ── Read endpoints: fills / positions / pnl / realized ─────────
// Mirror listPolyOpenOrdersViaRelay's shape so all read helpers behave
// the same: discriminated union with optional `code` on unavailable
// states. Defer-note item (fills/positions/pnl) was deferred when only
// orders shipped; these helpers + the corresponding hooks land it.

export interface PolyRelayFill {
  fill_id: string | null;
  order_id: string | null;
  ticker: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  count: number;
  price_cents: number;
  is_taker: boolean;
  fees_cents: number | null;
  executed_at: string;
}

export type ListPolyFillsResult =
  | { ok: true; fills: PolyRelayFill[] }
  | { ok: false; status: number; error: string; code?: PolyRelayUnavailableCode };

export async function listPolyFillsViaRelay(limit?: number): Promise<ListPolyFillsResult> {
  const qs = limit && limit > 0 ? `?limit=${encodeURIComponent(String(limit))}` : '';
  let res: Response;
  try {
    res = await apiFetch(`/api/polymarket/fills${qs}`, { method: 'GET' });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    return { ok: false, status: 0, error: message };
  }

  let body: { fills?: unknown; error?: string; code?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* non-JSON body */
  }

  if (res.ok) {
    const fills = Array.isArray(body.fills) ? (body.fills as PolyRelayFill[]) : [];
    return { ok: true, fills };
  }

  const code = parseUnavailableCode(body.code);
  return {
    ok: false,
    status: res.status,
    error: body.error ?? `Polymarket relay failed (${res.status})`,
    code,
  };
}

export interface PolyRelayPosition {
  ticker: string;
  side: 'yes' | 'no';
  netCount: number;
  avgEntryPriceCents: number;
  realizedPnlCents: number;
  feesPaidCents?: number;
}

export type ListPolyPositionsResult =
  | { ok: true; positions: PolyRelayPosition[] }
  | { ok: false; status: number; error: string; code?: PolyRelayUnavailableCode };

export async function listPolyPositionsViaRelay(): Promise<ListPolyPositionsResult> {
  let res: Response;
  try {
    res = await apiFetch('/api/polymarket/positions', { method: 'GET' });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    return { ok: false, status: 0, error: message };
  }

  let body: { positions?: unknown; error?: string; code?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* non-JSON body */
  }

  if (res.ok) {
    const positions = Array.isArray(body.positions)
      ? (body.positions as PolyRelayPosition[])
      : [];
    return { ok: true, positions };
  }

  const code = parseUnavailableCode(body.code);
  return {
    ok: false,
    status: res.status,
    error: body.error ?? `Polymarket relay failed (${res.status})`,
    code,
  };
}

export interface PolyRelayPnl {
  realizedPnlCents: number;
  openCount: number;
  openNotionalCents: number;
}

export type GetPolyPnlResult =
  | { ok: true; pnl: PolyRelayPnl }
  | { ok: false; status: number; error: string; code?: PolyRelayUnavailableCode };

export async function getPolyPnlViaRelay(): Promise<GetPolyPnlResult> {
  let res: Response;
  try {
    res = await apiFetch('/api/polymarket/pnl', { method: 'GET' });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    return { ok: false, status: 0, error: message };
  }

  let body: {
    realizedPnlCents?: unknown;
    openCount?: unknown;
    openNotionalCents?: unknown;
    error?: string;
    code?: string;
  } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* non-JSON body */
  }

  if (res.ok) {
    return {
      ok: true,
      pnl: {
        realizedPnlCents: Number(body.realizedPnlCents) || 0,
        openCount: Number(body.openCount) || 0,
        openNotionalCents: Number(body.openNotionalCents) || 0,
      },
    };
  }

  const code = parseUnavailableCode(body.code);
  return {
    ok: false,
    status: res.status,
    error: body.error ?? `Polymarket relay failed (${res.status})`,
    code,
  };
}

function parseUnavailableCode(code: string | undefined): PolyRelayUnavailableCode | undefined {
  return code === 'NOT_IN_ORG' ||
    code === 'PERSONAL_KEY_MODE' ||
    code === 'NO_POLY_CRED' ||
    code === 'ONLY_LEGACY_EOA_CRED'
    ? code
    : undefined;
}

// ── Post-deposit allowance refresh ─────────────────────────────
// Calls /api/polymarket/refresh-allowance which forces the CLOB to
// re-read on-chain balance + allowance for the org's stored credential
// triple and returns the fresh values. Required for sigType=3 (deposit
// wallet) accounts after every deposit; harmless for 0/1/2.

export interface PolyFunderBalance {
  collateralUsd: number;
  collateralAllowanceUsd: number;
}

export type RefreshPolyAllowanceResult =
  | { ok: true; balance: PolyFunderBalance }
  | { ok: false; status: number; error: string; code?: PolyRelayUnavailableCode };

export async function refreshPolyAllowanceViaRelay(): Promise<RefreshPolyAllowanceResult> {
  let res: Response;
  try {
    res = await apiFetch('/api/polymarket/refresh-allowance', { method: 'POST' });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    return { ok: false, status: 0, error: message };
  }

  let body: { balance?: unknown; error?: string; code?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* non-JSON body */
  }

  if (res.ok) {
    const b = (body.balance ?? {}) as Record<string, unknown>;
    return {
      ok: true,
      balance: {
        collateralUsd: Number(b.collateralUsd) || 0,
        collateralAllowanceUsd: Number(b.collateralAllowanceUsd) || 0,
      },
    };
  }

  const code = parseUnavailableCode(body.code);
  return {
    ok: false,
    status: res.status,
    error: body.error ?? `Polymarket relay failed (${res.status})`,
    code,
  };
}

export async function cancelPolyOrderViaRelay(
  orderId: string,
): Promise<CancelPolyOrderRelayResult> {
  let res: Response;
  try {
    res = await apiFetch(`/api/polymarket/orders/${encodeURIComponent(orderId)}`, {
      method: 'DELETE',
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    return { ok: false, status: 0, error: message };
  }

  let body: { result?: unknown; error?: string; code?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* non-JSON body */
  }

  if (res.ok) {
    return { ok: true, result: body.result };
  }

  const code =
    body.code === 'NOT_IN_ORG' ||
    body.code === 'PERSONAL_KEY_MODE' ||
    body.code === 'NO_POLY_CRED' ||
    body.code === 'ONLY_LEGACY_EOA_CRED'
      ? body.code
      : undefined;

  return {
    ok: false,
    status: res.status,
    error: body.error ?? `Polymarket relay failed (${res.status})`,
    code,
  };
}
